// type imports only: several specs mock the grid package without its runtime exports
import { type FilterColumnsArgs, type GetColumnForNewFilterArgs, type GridFilterItem, type GridFilterModel } from '@mui/x-data-grid-premium';

/** Login methods of the user list; `PASSWORD` stands for users without an OAuth provider. */
export const userLoginMethods = ['PASSWORD', 'AZURE', 'GOOGLE', 'GITHUB', 'OPENID', 'OKTA', 'SLACK', 'SAML', 'WEBAUTHN'] as const;

// grid columns and the `GET /users` parameter that filters them; `organisationId` would replace the context organisation
const filterParams: Record<string, string> = {
   firstName: 'firstName',
   lastName: 'lastName',
   email: 'email',
   oAuthProvider: 'oAuthProvider',
   organisationId: 'userOrganisationId',
   status: 'status',
};

const toParamValue = (value: unknown): string | null => {
   if (value === undefined || value === null || value === '') {
      return null;
   }

   if (Array.isArray(value)) {
      return value.length > 0 ? value.map((item) => toParamValue(item)).join(',') : null;
   }

   if (typeof value === 'object') {
      const { id } = value as { id?: number | string | null };
      return id === undefined || id === null ? null : String(id);
   }

   return typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean' ? String(value) : null;
};

/**
 * `GET /users` combines its filter parameters with AND and takes one value per parameter,
 * so the user list offers only AND and at most one filter per column.
 * Columns a filter row may pick: its own column and every column no other row filters yet. */
export function getUserListFilterColumns({ field, columns, currentFilters }: FilterColumnsArgs): string[] {
   return columns
      .filter((column) => column.field === field || !currentFilters.some((item) => item.field === column.field))
      .map((column) => column.field);
}

/** The first column without a filter yet, or `null` when every column has one, which stops adding filters. */
export function getUserListColumnForNewFilter({ currentFilters, columns }: GetColumnForNewFilterArgs): string | null {
   return columns.find((column) => !currentFilters.some((item) => item.field === column.field))?.field ?? null;
}

/**
 * Brings a filter model to what `GET /users` can express: AND (the grid's default without a logic operator),
 * and only the first filter of each column. Grid states saved before this restriction may still hold OR or
 * several filters on one column.
 */
export function toUserListFilterModel({ logicOperator: _logicOperator, ...filterModel }: GridFilterModel): GridFilterModel {
   const items = filterModel.items.reduce<GridFilterItem[]>(
      (unique, item) => (unique.some(({ field }) => field === item.field) ? unique : [...unique, item]),
      []
   );

   return { ...filterModel, items };
}

/**
 * Writes the user list's grid filters into the request parameters of `GET /users`.
 * Deleted users: the "is empty" filter hides them (the default), "is not empty" lists only them,
 * and without a filter on the column they are listed with the others.
 */
export function applyUserListFilters(urlParams: URLSearchParams, filterModel: GridFilterModel | undefined): URLSearchParams {
   const { items } = toUserListFilterModel(filterModel ?? { items: [] });

   Object.values(filterParams).forEach((param) => urlParams.delete(param));
   urlParams.delete('deleted');

   items.forEach(({ field, value }) => {
      const param = filterParams[field];
      const paramValue = toParamValue(value);

      if (param && paramValue !== null) {
         urlParams.set(param, paramValue);
      }
   });

   const deletedFilter = items.find(({ field }) => field === 'deletedAt');

   if (deletedFilter?.operator === 'isNotEmpty') {
      urlParams.set('deleted', 'only');
   } else if (deletedFilter?.operator !== 'isEmpty') {
      urlParams.set('deleted', String(true));
   }

   return urlParams;
}
